import { FadeUp } from "./Reveal";
import { Moon, Sparkles, UtensilsCrossed } from "lucide-react";
import { useLang } from "./LanguageContext";

const BASE = "https://customer-assets-gfyr7b9c.emergentagent.net/job_mozzarella-show/artifacts/";

const photoSrcs = {
  stretch: "/stage/lux-carlo-curd.webp",
  lift: "/stage/lux-lift.webp",
  knot: "/stage/lux-knot.webp",
  mise: "/stage/lux-mise.webp",
};
const journeySrcs = ["/stage/lux-journey-1.webp", "/stage/lux-journey-2.webp", "/stage/lux-journey-3.webp"];
const plateSrcs = ["/stage/tasting-1.webp", "/stage/tasting-2.webp", "/stage/tasting-3.webp"];
const videoSrcs = {
  stretch: { src: "/stage/live-stretch.mp4", poster: "/stage/live-stretch-poster.jpg" },
  hands: { src: "/stage/live-hands.mp4", poster: "/stage/live-hands-poster.jpg" },
  plating: { src: "/stage/live-plating.mp4", poster: "/stage/live-plating-poster.jpg" },
  pinsa: { src: `${BASE}po3x4d2z_WhatsApp%20Video%202026-08-29%20at%2018.54.02.mp4`, poster: "/stage/poster-pinsa.jpg" },
  finale: { src: "/stage/live-finale.mp4", poster: "/stage/live-finale-poster.jpg" },
};

const copy = {
  en: {
    kicker: "The Mediterranean Style",
    h2: <>One warm evening, <em className="italic">under the stars.</em></>,
    intro: "As evening falls, the experience becomes pure atmosphere. The casaro at work in the open air, warm steam rising from the pot, guests captivated by the gesture: an intimate, timeless theatre, staged in the most exclusive corner of the evening.",
    photos: {
      stretch: "Carlo, maestro casaro for three generations — the curd comes alive between his hands. Pure theatre",
      lift: "The casaro lifts the mozzarella, live among the palms",
      knot: "Each knot tied by hand, moments before serving",
      mise: "The mise en place — figs, herbs, tomatoes, ready for the pairing",
    },
    journey: ["Burrata, born in Puglia — shaped by hand over the warm whey", "The nodino, tied in seconds by expert hands", "From the pot to the plate — composed à la minute"],
    footageKicker: "Live Footage",
    footageH3: <>The show, <em className="italic">in motion.</em></>,
    footageBadge: "Live · That evening",
    videos: {
      stretch: "The stretch, live from the pot",
      hands: "Stracciatella, prepared by hand — live, in seconds",
      plating: "Plated à la minute, in front of the guests",
      pinsa: "Baked live, in the open air",
      finale: "The finale — every plate, presented by the chefs",
    },
    menuKicker: "Menu Degustazione",
    menuH3: <>And then, <em className="italic">the plate.</em></>,
    menuTitle: "The Tasting",
    menuSub: "Four acts · Composed à la minute",
    courses: [
      { name: "Warm Nodino", desc: "Hand-tied mozzarella knot, cold-pressed olive oil, sea salt — served minutes after the stretch" },
      { name: "Burrata al Momento", desc: "Burrata born in Puglia, shaped live over warm whey — fresh figs, heirloom tomatoes, toasted almonds" },
      { name: "Crispy Prawn", desc: "Golden-crusted prawn, avocado cream, physalis — a bright Mediterranean counterpoint" },
      { name: "Tartare in Almond Crust", desc: "Almond-crusted violet prawn tartare with a warm heart of burrata — the signature finale" },
    ],
    menuNote: "Each course is presented tableside, as it is finished.",
    plateAlts: ["Almond-crusted tartare & burrata — the signature plate", "Crispy prawn, avocado and physalis under the evening lights", "The tasting, served course after course"],
    notes: [
      { Icon: Moon, t: "An evening under the stars", d: "The show begins as the light fades — warm lamps, palms, the Mediterranean night as backdrop." },
      { Icon: Sparkles, t: "The magic of live craft", d: "Guests gather around the steaming pot and watch milk become mozzarella, in their hands minutes later." },
      { Icon: UtensilsCrossed, t: "A curated tasting", d: "Each stretch is paired with a refined mise en place — figs, heirloom tomatoes, fresh herbs, toasted almonds." },
    ],
  },
  it: {
    kicker: "The Mediterranean Style",
    h2: <>Una sera d'estate, <em className="italic">sotto le stelle.</em></>,
    intro: "Quando cala la sera, l'esperienza si fa pura atmosfera. Il casaro all'opera all'aperto, il vapore caldo che sale dal pentolone, gli ospiti rapiti dal gesto: un teatro intimo e senza tempo, messo in scena nell'angolo più esclusivo della serata.",
    photos: {
      stretch: "Carlo, maestro casaro da tre generazioni — la cagliata prende vita tra le sue mani. Puro teatro",
      lift: "Il casaro solleva la mozzarella, dal vivo tra le palme",
      knot: "Ogni nodo legato a mano, pochi istanti prima del servizio",
      mise: "La mise en place — fichi, erbe, pomodori, pronti per l'abbinamento",
    },
    journey: ["Burrata, nata in Puglia — formata a mano sul siero caldo", "Il nodino, legato in pochi secondi da mani esperte", "Dal pentolone al piatto — composto à la minute"],
    footageKicker: "Riprese dal Vivo",
    footageH3: <>Lo show, <em className="italic">in movimento.</em></>,
    footageBadge: "Live · Quella sera",
    videos: {
      stretch: "La filatura, dal vivo dal pentolone",
      hands: "La stracciatella, preparata a mano — dal vivo, in pochi secondi",
      plating: "Impiattato à la minute, davanti agli ospiti",
      pinsa: "Cotta dal vivo, all'aperto",
      finale: "Il finale — ogni piatto, presentato dagli chef",
    },
    menuKicker: "Menu Degustazione",
    menuH3: <>E poi, <em className="italic">il piatto.</em></>,
    menuTitle: "La Degustazione",
    menuSub: "Quattro atti · Composti à la minute",
    courses: [
      { name: "Nodino Caldo", desc: "Nodino di mozzarella legato a mano, olio extravergine a crudo, sale marino — servito pochi minuti dopo la filatura" },
      { name: "Burrata al Momento", desc: "Burrata nata in Puglia, formata dal vivo sul siero caldo — fichi freschi, pomodori antichi, mandorle tostate" },
      { name: "Gambero Croccante", desc: "Gambero in crosta dorata, crema di avocado, physalis — un contrappunto mediterraneo e luminoso" },
      { name: "Tartare in Crosta di Mandorle", desc: "Tartare di gambero viola in crosta di mandorle con cuore caldo di burrata — il finale d'autore" },
    ],
    menuNote: "Ogni portata è presentata al tavolo, appena finita.",
    plateAlts: ["Tartare in crosta di mandorle e burrata — il piatto d'autore", "Gambero croccante, avocado e physalis sotto le luci della sera", "La degustazione, servita portata dopo portata"],
    notes: [
      { Icon: Moon, t: "Una sera sotto le stelle", d: "Lo show inizia quando la luce svanisce — lampade calde, palme, la notte mediterranea come sfondo." },
      { Icon: Sparkles, t: "La magia del mestiere dal vivo", d: "Gli ospiti si raccolgono attorno al pentolone fumante e vedono il latte diventare mozzarella, tra le loro mani pochi minuti dopo." },
      { Icon: UtensilsCrossed, t: "Una degustazione curata", d: "Ogni filatura è abbinata a una mise en place raffinata — fichi, pomodori antichi, erbe fresche, mandorle tostate." },
    ],
  },
  ru: {
    kicker: "The Mediterranean Style",
    h2: <>Тёплый вечер, <em className="italic">под звёздами.</em></>,
    intro: "Когда наступает вечер, опыт превращается в чистую атмосферу. Казаро работает под открытым небом, тёплый пар поднимается над котлом, гости заворожены жестом: камерный, вневременной театр в самом эксклюзивном уголке вечера.",
    photos: {
      stretch: "Карло, мастер-казаро в третьем поколении — сгусток оживает в его руках. Чистый театр",
      lift: "Казаро поднимает моцареллу, вживую среди пальм",
      knot: "Каждый узел завязан вручную, за мгновения до подачи",
      mise: "Mise en place — инжир, травы, томаты, готовы к подаче",
    },
    journey: ["Буррата, родом из Апулии — сформована вручную над тёплой сывороткой", "Нодино, завязанный за секунды опытными руками", "Из котла на тарелку — à la minute"],
    footageKicker: "Живая съёмка",
    footageH3: <>Шоу, <em className="italic">в движении.</em></>,
    footageBadge: "Live · Тот вечер",
    videos: {
      stretch: "Вытягивание, вживую из котла",
      hands: "Страчателла, приготовленная вручную — вживую, за секунды",
      plating: "Подано à la minute, на глазах у гостей",
      pinsa: "Испечено вживую, под открытым небом",
      finale: "Финал — каждая тарелка, представленная шефами",
    },
    menuKicker: "Дегустационное меню",
    menuH3: <>А затем — <em className="italic">тарелка.</em></>,
    menuTitle: "Дегустация",
    menuSub: "Четыре акта · À la minute",
    courses: [
      { name: "Тёплый нодино", desc: "Узел моцареллы, завязанный вручную, масло первого отжима, морская соль" },
      { name: "Burrata al Momento", desc: "Буррата из Апулии, сформованная вживую — инжир, старинные томаты, жареный миндаль" },
      { name: "Хрустящая креветка", desc: "Золотистая корочка, крем из авокадо, физалис" },
      { name: "Тартар в миндальной корочке", desc: "Тартар из фиолетовой креветки в миндальной корочке с тёплым сердцем бурраты — фирменный финал" },
    ],
    menuNote: "Каждое блюдо подаётся у стола, сразу по готовности.",
    plateAlts: ["Тартар в миндале и буррата — фирменная тарелка", "Хрустящая креветка, авокадо и физалис под вечерними огнями", "Дегустация, блюдо за блюдом"],
    notes: [
      { Icon: Moon, t: "Вечер под звёздами", d: "Шоу начинается с угасанием света — тёплые лампы, пальмы, средиземноморская ночь." },
      { Icon: Sparkles, t: "Магия живого ремесла", d: "Гости собираются вокруг дымящегося котла и видят, как молоко становится моцареллой." },
      { Icon: UtensilsCrossed, t: "Продуманная дегустация", d: "Каждое вытягивание сопровождает изысканная mise en place — инжир, томаты, травы, миндаль." },
    ],
  },
  es: {
    kicker: "The Mediterranean Style",
    h2: <>Una noche cálida, <em className="italic">bajo las estrellas.</em></>,
    intro: "Cuando cae la tarde, la experiencia se vuelve pura atmósfera. El casaro trabajando al aire libre, el vapor cálido subiendo de la olla, los invitados cautivados por el gesto: un teatro íntimo y sin tiempo, puesto en escena en el rincón más exclusivo de la velada.",
    photos: {
      stretch: "Carlo, maestro casaro de tres generaciones — la cuajada cobra vida entre sus manos. Puro teatro",
      lift: "El casaro levanta la mozzarella, en vivo entre las palmeras",
      knot: "Cada nudo atado a mano, momentos antes de servir",
      mise: "La mise en place — higos, hierbas, tomates, listos para el maridaje",
    },
    journey: ["Burrata, nacida en Puglia — formada a mano sobre el suero tibio", "El nodino, atado en segundos por manos expertas", "De la olla al plato — compuesto à la minute"],
    footageKicker: "Tomas en Vivo",
    footageH3: <>El show, <em className="italic">en movimiento.</em></>,
    footageBadge: "Live · Esa noche",
    videos: {
      stretch: "El hilado, en vivo desde la olla",
      hands: "La stracciatella, preparada a mano — en vivo, en segundos",
      plating: "Emplatado à la minute, frente a los invitados",
      pinsa: "Horneada en vivo, al aire libre",
      finale: "El final — cada plato, presentado por los chefs",
    },
    menuKicker: "Menú Degustación",
    menuH3: <>Y luego, <em className="italic">el plato.</em></>,
    menuTitle: "La Degustación",
    menuSub: "Cuatro actos · Compuestos à la minute",
    courses: [
      { name: "Nodino Tibio", desc: "Nudo de mozzarella atado a mano, aceite de oliva en crudo, sal marina — servido minutos después del hilado" },
      { name: "Burrata al Momento", desc: "Burrata nacida en Puglia, formada en vivo sobre el suero tibio — higos frescos, tomates antiguos, almendras tostadas" },
      { name: "Camarón Crujiente", desc: "Camarón en costra dorada, crema de aguacate, physalis — un contrapunto mediterráneo y luminoso" },
      { name: "Tartar en Costra de Almendras", desc: "Tartar de gamba violeta en costra de almendras con corazón tibio de burrata — el final de autor" },
    ],
    menuNote: "Cada plato se presenta en la mesa, recién terminado.",
    plateAlts: ["Tartar en costra de almendras y burrata — el plato de autor", "Camarón crujiente, aguacate y physalis bajo las luces de la noche", "La degustación, servida plato tras plato"],
    notes: [
      { Icon: Moon, t: "Una noche bajo las estrellas", d: "El show comienza cuando la luz se apaga — lámparas cálidas, palmeras, la noche mediterránea como telón de fondo." },
      { Icon: Sparkles, t: "La magia del oficio en vivo", d: "Los invitados se reúnen alrededor de la olla humeante y ven la leche convertirse en mozzarella, en sus manos minutos después." },
      { Icon: UtensilsCrossed, t: "Una degustación curada", d: "Cada hilado se marida con una mise en place refinada — higos, tomates antiguos, hierbas frescas, almendras tostadas." },
    ],
  },
};

const Fig = ({ frame, cap, capSize = "text-sm md:text-base", testid, children }) => (
  <figure className="group h-full flex flex-col">
    <div className={`relative overflow-hidden rounded-sm ${frame}`}>{children}</div>
    {cap && (
      <figcaption className="mt-3">
        <span className={`font-serif italic text-bg/75 ${capSize}`} data-testid={testid}>{cap}</span>
      </figcaption>
    )}
  </figure>
);

export const MediterraneanStage = ({ luxury = false }) => {
  const { lang } = useLang();
  const t = copy[lang];
  return (
    <section id="mediterranean-stage" className="py-24 md:py-40 bg-[#120a0c] text-bg overflow-hidden" data-testid="mediterranean-stage-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <FadeUp className="mb-14 md:mb-20 max-w-2xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-5">{t.kicker}</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05]">{t.h2}</h2>
          <p className="font-sans text-base md:text-lg text-bg/70 mt-8 leading-relaxed">{t.intro}</p>
        </FadeUp>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 items-stretch">
          <FadeUp className="lg:col-span-7">
            <Fig frame="aspect-[3/4] lg:aspect-auto lg:flex-1 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]" cap={t.photos.stretch} capSize="text-base md:text-xl" testid="main-photo-caption">
              <img src={photoSrcs.stretch} alt={t.photos.stretch} data-testid="mediterranean-stage-main-photo" className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
            </Fig>
          </FadeUp>

          <div className="lg:col-span-5 grid grid-cols-2 lg:grid-cols-1 gap-4 md:gap-5">
            <FadeUp delay={0.08}>
              <Fig frame="aspect-[3/2]" cap={t.photos.lift}>
                <img src={photoSrcs.lift} alt={t.photos.lift} className="w-full h-full object-cover object-[50%_30%] transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              </Fig>
            </FadeUp>
            <FadeUp delay={0.16}>
              <Fig frame="aspect-[3/2]" cap={t.photos.knot}>
                <img src={photoSrcs.knot} alt={t.photos.knot} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              </Fig>
            </FadeUp>
            <FadeUp delay={0.24} className="col-span-2 lg:col-span-1">
              <Fig frame="aspect-[3/2]" cap={t.photos.mise}>
                <img src={photoSrcs.mise} alt={t.photos.mise} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              </Fig>
            </FadeUp>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5 mt-4 md:mt-5">
          {journeySrcs.map((src, i) => (
            <FadeUp key={src} delay={i * 0.08}>
              <Fig frame="aspect-[4/3]" cap={t.journey[i]}>
                <img src={src} alt={t.journey[i]} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              </Fig>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-16 md:mt-24 mb-8 max-w-xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-4">{t.footageKicker}</p>
          <h3 className="font-serif text-3xl md:text-4xl leading-tight">{t.footageH3}</h3>
        </FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 items-stretch" data-testid="live-footage-grid">
          <FadeUp className="lg:col-span-7">
            <Fig frame="aspect-[4/3] lg:aspect-auto lg:flex-1 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]" cap={t.videos.stretch} capSize="text-base md:text-xl">
              <video src={videoSrcs.stretch.src} poster={videoSrcs.stretch.poster} autoPlay muted loop playsInline data-testid="live-footage-main-video" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute top-5 left-5 flex items-center gap-2 bg-black/50 backdrop-blur-md rounded-full px-4 py-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-terracotta opacity-75 animate-ping" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-terracotta" />
                </span>
                <span className="font-sans text-[11px] uppercase tracking-[0.25em] text-bg">{t.footageBadge}</span>
              </div>
            </Fig>
          </FadeUp>
          <div className="lg:col-span-5 grid grid-cols-2 gap-4 md:gap-5">
            {(luxury ? ["hands", "plating", "finale"] : ["hands", "plating", "pinsa", "finale"]).map((k, idx) => (
              <FadeUp key={k} className={luxury && idx === 2 ? "col-span-2" : ""}>
                <Fig frame="aspect-[4/3]" cap={t.videos[k]}>
                  <video src={videoSrcs[k].src} poster={videoSrcs[k].poster} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" />
                </Fig>
              </FadeUp>
            ))}
          </div>
        </div>

        <FadeUp className="mt-16 md:mt-24 mb-8 max-w-xl">
          <p className="font-sans text-xs uppercase tracking-[0.35em] text-terracotta mb-4">{t.menuKicker}</p>
          <h3 className="font-serif text-3xl md:text-4xl leading-tight">{t.menuH3}</h3>
        </FadeUp>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 items-stretch" data-testid="tasting-menu-grid">
          <FadeUp className="lg:col-span-5">
            <div className="h-full bg-bg text-ink rounded-sm p-8 md:p-10 flex flex-col border border-bg/20" data-testid="tasting-menu-card">
              <div className="text-center border-b border-ink/15 pb-6 mb-6">
                <p className="font-sans text-[10px] uppercase tracking-[0.35em] text-terracotta mb-3">{t.menuKicker}</p>
                <h4 className="font-serif text-3xl md:text-4xl">{t.menuTitle}</h4>
                <p className="font-sans text-xs uppercase tracking-[0.2em] text-ink-soft mt-2">{t.menuSub}</p>
              </div>
              <div className="flex-1 space-y-6">
                {t.courses.map((c, i) => (
                  <div key={c.name} data-testid={`menu-course-${i + 1}`}>
                    <div className="flex items-baseline gap-3">
                      <span className="font-serif italic text-2xl text-terracotta/80 leading-none">{["I", "II", "III", "IV"][i]}</span>
                      <h5 className="font-serif text-xl md:text-2xl">{c.name}</h5>
                    </div>
                    <p className="font-sans text-sm text-ink-soft leading-relaxed mt-1.5 pl-9">{c.desc}</p>
                    {i < t.courses.length - 1 && <div className="mt-6 text-center text-terracotta/50 text-xs" aria-hidden="true">✦</div>}
                  </div>
                ))}
              </div>
              <p className="font-sans text-xs italic text-ink-soft/80 text-center border-t border-ink/15 pt-5 mt-8">{t.menuNote}</p>
            </div>
          </FadeUp>
          <div className="lg:col-span-7 grid grid-cols-1 gap-4 md:gap-5">
            <FadeUp>
              <Fig frame="aspect-[16/9]" cap={t.plateAlts[0]} capSize="text-base md:text-lg">
                <img src={plateSrcs[0]} alt={t.plateAlts[0]} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
              </Fig>
            </FadeUp>
            <div className="grid grid-cols-2 gap-4 md:gap-5">
              {[1, 2].map((i) => (
                <FadeUp key={i} delay={i * 0.08}>
                  <Fig frame="aspect-[4/3]" cap={t.plateAlts[i]}>
                    <img src={plateSrcs[i]} alt={t.plateAlts[i]} className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-105" />
                  </Fig>
                </FadeUp>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mt-14 md:mt-20 border-t border-bg/15 pt-12">
          {t.notes.map((n) => (
            <FadeUp key={n.t}>
              <n.Icon className="h-7 w-7 text-terracotta mb-4" strokeWidth={1.5} />
              <h3 className="font-serif text-2xl md:text-3xl mb-2">{n.t}</h3>
              <p className="font-sans text-sm md:text-base text-bg/70 leading-relaxed max-w-xs">{n.d}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};
